import Vue from 'vue';
import Router from '@plugins/VueRouter/index';
import auth from '@app2/modules/Auth/auth.routes';
import register from '@app2/modules/Register/register.routes';
import onboarding from '@app2/modules/Onboarding/onboarding.routes';
import audience from '@app2/modules/Audience/audience.routes';
import hub from '@app2/modules/Hubs/hubs.routes';
import dashboard from '@app2/modules/Dashboard/dashboard.routes';
import apps from '@app2/modules/Apps/apps.routes';
import library from '@app2/modules/Library/library.routes';
import account from '@app2/modules/AccountSettings/account.routes';
import store from '@app2/store';
import gettingStarted from '@app2/modules/GettingStarted/getting-started.routes'
import { addGuardstoRoutes, checkEditors, manualMultiguard } from '@app2/router/guards';
import eventBus from '@app2/core/eventBus';
import axios from '@app2/api/axios';
import Hub from '@app2/models/Hub'
import rootApp from '@app2/app';

Vue.use(Router);



// --- Reusable function to determine the redirect target --- 
async function getLatestHubRedirectTarget() {
  // Get user ID from store if available
  const userId = store.getters['auth/GET_USER']?.id || 'default';
  // Get team ID from store if available
  const teamId = store.getters['auth/GET_USER']?.current_team_id || 'default';
  // User-specific and team-specific storage key with less obvious naming
  const pinnedHubsDetailsKey = `ph_${userId}_${teamId}`;
  // Legacy keys for backward compatibility
  const legacyUserKey = `pinnedHubsDetails_${userId}`;
  const legacyKey = 'pinnedHubsDetails';
  let targetHash = null;

  // 1. Try to get the first pinned hub's hash from localStorage (now stores objects)
  try {
    // First try user-and-team-specific key
    let storedHubDetails = localStorage.getItem(pinnedHubsDetailsKey);
    
    // If not found, try user-specific key for backward compatibility
    if (!storedHubDetails) {
      const legacyUserData = localStorage.getItem(legacyUserKey);
      if (legacyUserData) {
        storedHubDetails = legacyUserData;
        // Migrate data to user-and-team-specific key
        localStorage.setItem(pinnedHubsDetailsKey, legacyUserData);
        // Don't remove legacy user key as it might be needed for other teams
      }
    }
    
    // If still not found, try legacy key for backward compatibility
    if (!storedHubDetails) {
      const legacyData = localStorage.getItem(legacyKey);
      if (legacyData) {
        storedHubDetails = legacyData;
        // Migrate data to user-and-team-specific key
        localStorage.setItem(pinnedHubsDetailsKey, legacyData);
        // Remove legacy data
        localStorage.removeItem(legacyKey);
      }
    }
    if (storedHubDetails) {
      const parsedHubs = JSON.parse(storedHubDetails);
      // Check if it's an array of objects, has items, and the first item has a hash property
      if (Array.isArray(parsedHubs) && 
          parsedHubs.length > 0 && 
          parsedHubs[0] && 
          typeof parsedHubs[0].hash === 'string') {
        targetHash = parsedHubs[0].hash; // Get hash from the first object
      }
    }
  } catch (error) {
    console.error('Error reading or parsing pinned hubs from localStorage for redirect:', error);
    // Clear potentially corrupted data
    try {
      localStorage.removeItem(pinnedHubsDetailsKey);
      // Also remove legacy keys if they exist
      if (localStorage.getItem(legacyUserKey)) {
        localStorage.removeItem(legacyUserKey);
      }
      if (localStorage.getItem(legacyKey)) {
        localStorage.removeItem(legacyKey);
      }
    } catch (removeError) { /* Ignore */ }
  }

  // 2. If no valid hash from localStorage, fetch the latest from API
  if (!targetHash) {
    try {
      const response = await axios.get('/api/hubs?sort=-updated_at&limit=1&fields=hash,updated_at');
      const latestHub = response?.data?.data?.[0];
      if (latestHub?.hash) {
        targetHash = latestHub.hash;
      }
    } catch (apiError) {
      console.error('Error fetching latest hub from API:', apiError);
      // Proceed with targetHash as null if API fails
    }
  }

  // 3. Determine redirect target
  // If we have a target hash (from localStorage or API), redirect to its preview.
  // Otherwise (no hubs exist or errors occurred), redirect to the Library Listing.
  return {
    name: targetHash ? 'hubPreviewDashboard' : 'LibraryListing',
    ...(targetHash ? { params: { hash: targetHash } } : {}),
    replace: true
  };
}

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      name: 'home',
      beforeEnter: async (to, from, next) => { 
        try {
          const redirectTarget = await getLatestHubRedirectTarget();
          redirectTarget.replace = true;
          next(redirectTarget);
        } catch (error) {
          next({ name: 'LibraryListing', replace: true });
        }
      },
    },
    ...checkEditors([
      ...auth,
      ...account,
      ...onboarding,
      ...register,
      ...addGuardstoRoutes([
        ...audience,
        ...hub,
        ...apps,
        ...library,
        ...dashboard,
        ...gettingStarted,
        { path: '/community', name: 'community', component: () => import('@app2/modules/Community/Index.vue'), meta: {hideSidenavBackButton: true, showSubnav: false} },
        { path: '/', name: 'home', redirect: { name: 'dashboard' } },
      ]),
      { path: "*", redirect: { name: 'dashboard' } }
    ]),
    {
      path: '/watch/:hash/', 
      name: 'watch',
      component: () => import('@app2/modules/Watch/Index.vue'),
      props: true
    }
  ],
  linkActiveClass: 'active',
  linkExactActiveClass: 'active',
});

router.afterEach((to, from) => {
  store.commit('setLoadedFirstRoute', true);
  eventBus.$emit('posthog:page-view');
  setTimeout(() => {
    if ( store.state.v2.apps.isModalOpen ) return;
    window.scrollTo(0, 0);
  }, 100);
});

router.beforeEach(async (to, from, next) => {

  if (to.name === 'hub' && to.params.hash) {
    next({
      name: 'hubPreviewDashboard', 
      params: { hash: to.params.hash }
    });
    return;
  }

  if (to.params.hash && to.path.includes('/hubs/')) {
    try {
      const hub = Hub.find(to.params.hash);
      if (hub) {
        next();
      } else {
        await Hub.api().get(to.params.hash);
        next();
      }
    } catch (error) {
      const redirectTarget = await getLatestHubRedirectTarget();
      rootApp.$toasted.show('Hub could not be loaded :(', { type: 'error' });
      next(redirectTarget);
    }
  }

  if (to.name === 'hubSettingsPushNotifications') {
    let {response} = await Hub.api().get(to.params.hash);
    if (response.data && !response.data.has_mobile_app) {
      next({path: `hubs/${to.params.hash}`});
      return;
    }
    next();
    return;
  }

  if (to.name === 'hubs') {
    const redirectTarget = await getLatestHubRedirectTarget();
    if (to.query.action === 'create_from_template') {
      next(Object.assign(redirectTarget, {query: to.query}));
      return;
    }
    next(redirectTarget);
    return;
  }

  if (to.name === 'dashboard') {
    const redirectTarget = await getLatestHubRedirectTarget();
    next(redirectTarget);
    return;
  }

  if ( to.name === 'AccountSubscription' && to.query.fml ) {
    const { data: {is_qualified} } = await axios.get('/api/internal/v2/fml/qualified')
    if ( is_qualified ) return next();
    await axios.put('/api/internal/v2/fml', {
      is_fml: true
    })
    await store.dispatch('auth/fetchUser');
    return next('/dashboard');
  }
  if ( to.meta.ph ) {
    eventBus.$emit('posthog:start-recording');
  } else {
    eventBus.$emit('posthog:stop-recording');
  }
  const queryParams = new URLSearchParams(to.query);
  const hasIdParam = queryParams.has('id');
  const hasStateParam = queryParams.has('state');

  if (hasIdParam && hasStateParam && to.meta.checkAndRemoveQueryParams) {
    store.commit('auth/STORE_IS_NEW_SUBSCRIPTION', true);
    queryParams.delete('id');
    queryParams.delete('state');
    next({
      path: to.path,
      query: queryParams.toString()
    })
    return;
  }

  const routeNames = ['dashboard', 'hubs', 'hub', 'hubEditorDashboard', 'hubPreviewDashboard', 'audience', 'apps', 'exploreApps']; // beforeEnter alternative
  if (routeNames.includes(to.name)) {
    manualMultiguard(to, from, next);
  } else {
    next()
  }
})

export default router;
